/*
 * Bloomeroo cart postcode availability gate.
 *
 * After a delivery postcode is entered, flags any cart line item that isn't
 * available for the resolved state and blocks checkout until every remaining
 * item is available. Fail-open: a blank/missing state_availability_computed, an
 * unresolvable postcode, or a parse error never blocks — only an explicit
 * `false` for the resolved state does.
 *
 * Read-only towards the theme's postcode capture and delivery-date picker: it
 * only reads the postcode and toggles the theme's existing checkout gate
 * (the #bl-availability-gate required field + enableSubmit()).
 */
(function () {
  if (window.__blCartAvailLoaded) return;
  window.__blCartAvailLoaded = true;

  var STATE_LABEL = { nsw: 'NSW', vic: 'VIC', qld: 'QLD', sa: 'SA', wa: 'WA', act: 'ACT' };

  // Where "Shop alternatives" sends a shopper blocked by a metro-only product.
  var ALTERNATIVES_URL = '/collections/all';

  /*
   * Tasmania is closed to fresh flowers and plants under Bass Strait
   * biosecurity rules, so a floral line can't be delivered there however well
   * stocked it is. Non-floral gifts still go across, which is why this blocks
   * the floral lines rather than the whole order.
   *
   * Deliberately a separate check from postcodeToState(), which maps TAS to
   * 'vic' because Melbourne holds the stock. That answers "is it in stock";
   * this answers "may it cross Bass Strait". Different questions.
   *
   * 7000-7315 is BL's entire serviced TAS range.
   */
  function isTasmanian(pc) {
    if (!pc) return false;
    pc = String(pc).trim();
    if (!/^\d{4}$/.test(pc)) return false;
    return pc >= '7000' && pc <= '7315';
  }

  // 4-digit AU postcode -> the state key used in state_availability_computed.
  // Complete ranges (matches Bloomeroo's existing assignTimeZoneForPostcode);
  // ACT -> nsw, TAS -> vic, NT -> vic. String compares on equal-length codes.
  function postcodeToState(pc) {
    if (!pc) return null;
    pc = String(pc).trim();
    if (!/^\d{4}$/.test(pc)) return null;
    // ACT (carve out first — its ranges sit inside NSW) -> nsw
    if ((pc >= '0200' && pc <= '0299') ||
        (pc >= '2600' && pc <= '2618') ||
        (pc >= '2900' && pc <= '2920')) return 'nsw';
    // NSW
    if ((pc >= '1000' && pc <= '2599') ||
        (pc >= '2619' && pc <= '2898') ||
        (pc >= '2921' && pc <= '2999')) return 'nsw';
    // VIC
    if ((pc >= '3000' && pc <= '3999') ||
        (pc >= '8000' && pc <= '8999')) return 'vic';
    // QLD
    if ((pc >= '4000' && pc <= '4999') ||
        (pc >= '9000' && pc <= '9999')) return 'qld';
    // SA
    if (pc >= '5000' && pc <= '5999') return 'sa';
    // WA
    if ((pc >= '6000' && pc <= '6797') ||
        (pc >= '6800' && pc <= '6999')) return 'wa';
    // TAS -> VIC (Melbourne fulfilment)
    if (pc >= '7000' && pc <= '7999') return 'vic';
    // NT -> VIC (Melbourne fulfilment)
    if (pc >= '0800' && pc <= '0999') return 'vic';
    return null;
  }
  window.blPostcodeToState = postcodeToState;

  // Live #postcode value if it's a valid 4-digit code, else the persisted
  // cart attribute rendered onto #cart_form (survives the qty/remove reload).
  function resolvedZip() {
    var input = document.getElementById('postcode');
    if (input && /^\d{4}$/.test((input.value || '').trim())) return input.value.trim();
    var form = document.getElementById('cart_form');
    if (form) {
      var z = (form.getAttribute('data-cart-zip') || '').trim();
      if (/^\d{4}$/.test(z)) return z;
    }
    return null;
  }

  /*
   * metro-only support.
   *
   * A product tagged 'metro-only' can only go to a same-day (metro) postcode.
   * Same-day status comes from the same postcode dataset the delivery-date
   * picker already fetches on this page, so this is a browser cache hit rather
   * than a second download.
   *
   * Fail-open throughout: until the dataset lands, or if it fails, or if the
   * postcode isn't in it, sameDayFor() returns null and nothing is blocked.
   * Only an explicit next-day postcode blocks — the same stance the state gate
   * takes in only acting on an explicit `false`.
   */
  var sameDayMap = null;

  function loadSameDayMap() {
    var url = window.blPostcodeDataUrl;
    if (!url || typeof fetch !== 'function') return;
    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var map = {};
        (data.postcodes || []).forEach(function (row) {
          var pc = String(row.postcode == null ? '' : row.postcode).trim();
          if (!pc) return;
          // A postcode repeats once per suburb; same-day if any of its rows is.
          if (map[pc] !== true) {
            map[pc] = String(row.sameday).toUpperCase() === 'TRUE';
          }
        });
        sameDayMap = map;
        validate(); // re-run now the data is in
      })
      .catch(function () { /* fail open: metro-only never blocks */ });
  }

  // true | false | null  (null = unknown, never blocks)
  function sameDayFor(zip) {
    if (!zip || !sameDayMap) return null;
    var v = sameDayMap[zip];
    return typeof v === 'boolean' ? v : null;
  }

  function lineAvailable(line, state) {
    if (!state) return true;
    var raw = line.getAttribute('data-state-availability');
    if (!raw) return true; // no computed data -> available (fail-open)
    var data;
    try { data = JSON.parse(raw); } catch (e) { return true; }
    if (!data || typeof data[state] !== 'boolean') return true;
    return data[state];
  }

  // Drive the theme's own gate: an empty required field keeps .checkout_clone
  // disabled via enableSubmit(); "ok" lets the theme own the enabled state.
  function setGate(blocked) {
    window.__blCartAvailBlocked = !!blocked;
    var gate = document.getElementById('bl-availability-gate');
    if (gate) gate.value = blocked ? '' : 'ok';
    if (typeof window.enableSubmit === 'function') {
      try { window.enableSubmit(); } catch (e) {}
    } else if (blocked) {
      var clone = document.querySelector('.checkout_clone');
      if (clone) clone.setAttribute('disabled', 'disabled');
    }
    // The mobile sticky checkout button isn't a .field--required button, so
    // enableSubmit() can't grey it. Reflect the blocked state directly: keep it
    // visible but greyed (CSS), while the capture-phase guard blocks the tap.
    var mobileBtn = document.querySelector('.mobile-sticky-checkout-button');
    if (mobileBtn) mobileBtn.classList.toggle('bl-cart-checkout-blocked', !!blocked);
  }

  // Pin the summary just above the mobile sticky checkout button (whose height
  // varies). The fixed positioning itself is gated to mobile in CSS; here we
  // only feed it the right bottom offset.
  function positionStickySummary(summary) {
    if (!summary || summary.hidden) return;
    var btn = document.querySelector('.mobile-sticky-checkout-button');
    var h = (btn && btn.offsetHeight) ? btn.offsetHeight : 0;
    summary.style.bottom = (h + 8) + 'px';
  }

  function validate() {
    var zip = resolvedZip();
    var state = postcodeToState(zip);
    var label = state ? (STATE_LABEL[state] || state.toUpperCase()) : '';
    var sameDay = sameDayFor(zip);
    var tasmania = isTasmanian(zip);
    var lines = document.querySelectorAll('#cart_form [data-bl-cart-line]');
    var invalid = 0;
    var metroCount = 0;
    var tasCount = 0;

    lines.forEach(function (line) {
      var old = line.querySelector('.bl-cart-avail-warning');
      if (old && old.parentNode) old.parentNode.removeChild(old);

      // Order matters. Tasmania first: it's an absolute rule, so naming stock
      // or metro there would send the shopper chasing a fix that doesn't exist.
      // Then metro-only, which is more specific than the state gate.
      var reason = '';
      if (tasmania && line.hasAttribute('data-has-flowers')) {
        reason = 'tas';
      } else if (line.hasAttribute('data-metro-only') && sameDay === false) {
        reason = 'metro';
      } else if (state && !lineAvailable(line, state)) {
        reason = 'state';
      }
      if (!reason) return;

      invalid++;
      if (reason === 'metro') metroCount++;
      if (reason === 'tas') tasCount++;

      var title = line.getAttribute('data-product-title') || 'This item';
      var removeUrl = line.getAttribute('data-remove-url') || '';

      var warn = document.createElement('div');
      warn.className = 'bl-cart-avail-warning';

      var msg = document.createElement('span');
      msg.className = 'bl-cart-avail-msg';
      if (reason === 'tas') {
        msg.textContent = 'Sorry, ' + title + " can't be delivered to Tasmania." +
          ' Biosecurity rules don’t allow fresh flowers or plants across Bass Strait.' +
          ' Non-floral gifts can still be sent.';
      } else if (reason === 'metro') {
        msg.textContent = 'Sorry, ' + title +
          ' is only available for delivery to metro areas. Please select another product.';
      } else {
        msg.textContent = title + " isn't available for delivery to " + label + '.';
      }
      warn.appendChild(msg);

      if (reason === 'metro' || reason === 'tas') {
        var shop = document.createElement('a');
        shop.className = 'bl-cart-avail-shop';
        shop.setAttribute('href', ALTERNATIVES_URL);
        shop.textContent = 'Shop alternatives';
        warn.appendChild(shop);
      }

      if (removeUrl) {
        var rm = document.createElement('a');
        rm.className = 'bl-cart-avail-remove';
        rm.setAttribute('href', removeUrl); // native theme remove -> reload -> re-validate
        rm.textContent = 'Remove';
        warn.appendChild(rm);
      }

      (line.querySelector('.cart-details') || line).appendChild(warn);
    });

    // Summary box above the checkout button.
    var summary = document.getElementById('bl-cart-avail-summary');
    if (summary) {
      if (invalid > 0) {
        var noun = invalid === 1 ? 'item' : 'items';
        var verb = invalid === 1 ? "isn't" : "aren't";
        var them = invalid === 1 ? 'it' : 'them';
        if (tasCount === invalid) {
          summary.textContent = invalid + ' ' + noun + ' in your cart ' + verb +
            " able to be sent to Tasmania. Biosecurity rules don’t allow fresh flowers" +
            ' or plants. Please remove ' + them + ' to continue with your other gifts.';
        } else if (metroCount === invalid) {
          // Every blocked line is metro-only, so name that reason rather than
          // a state the item may well be in stock for.
          summary.textContent = invalid + ' ' + noun + ' in your cart can only be' +
            ' delivered to metro areas. Please remove ' + them + ' or choose another product.';
        } else {
          summary.textContent = invalid + ' ' + noun + ' in your cart ' + verb +
            ' available for delivery to ' + label + '. Please remove ' + them + ' before proceeding.';
        }
        summary.hidden = false;
        summary.classList.add('bl-summary-sticky');
        positionStickySummary(summary);
      } else {
        summary.hidden = true;
        summary.textContent = '';
        summary.classList.remove('bl-summary-sticky');
        summary.style.bottom = '';
      }
    }

    setGate(invalid > 0);
  }
  window.blValidateCartAvailability = validate;

  // Hard guard: block the checkout action while any item is unavailable. Capture
  // phase so it runs before the theme's bubble-phase jQuery handlers; bound on
  // the mobile sticky button too, which programmatically triggers the clone.
  function clickGuard(e) {
    if (!window.__blCartAvailBlocked) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    return false;
  }

  function bindGuards() {
    ['.checkout_clone', '.mobile-sticky-checkout-button'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.__blGuarded) return;
        el.addEventListener('click', clickGuard, true);
        el.__blGuarded = true;
      });
    });
  }

  function init() {
    bindGuards();
    loadSameDayMap();
    validate();
  }

  // Re-validate on postcode entry/change (debounced). Qty change and item
  // removal both reload /cart, so init() on load covers those.
  var t;
  function schedule() { clearTimeout(t); t = setTimeout(validate, 150); }
  document.addEventListener('input', function (e) {
    if (e.target && e.target.id === 'postcode') schedule();
  }, true);
  document.addEventListener('change', function (e) {
    if (e.target && e.target.id === 'postcode') schedule();
  }, true);
  window.addEventListener('resize', function () {
    var summary = document.getElementById('bl-cart-avail-summary');
    if (summary && !summary.hidden) positionStickySummary(summary);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Turbo / InstantClick SPA navigation onto the cart.
  if (window.InstantClick && typeof window.InstantClick.on === 'function') {
    window.InstantClick.on('change', init);
  }
})();
